import {
  partialUpdateEventRoles,
  EventRole,
  createEventRoles,
  listEventRoles,
  destroyEventRoles,
  updateRanksEventRoles,
  magiclinkUrlUser,
  presignedS3,
  listEmail,
  listRole,
  partialUpdateEventRolesV2,
  PartialUpdateEventRolesV2Props,
  partialUpdateEventDefaultRoles,
  listFetchDefaultRoles,
  PartialUpdateEventDefaultRolesProps
} from '@goldcast/api/backend';
import axios from 'axios';

import { ExtendedRole, predeterminedSpeakerRoles, Speaker, SpeakerRoleName, User } from './types';
import { transformServerSpeakers } from './utils';

import { uuidGen, getFileBlob } from '@/utils/utils.js';

export function uploadUserImage({ imageFile, userId }: { imageFile: File; userId: string }) {
  if (!imageFile) {
    return Promise.resolve('');
  }

  const imageUUID = uuidGen();
  const imageFileExtension = (imageFile.name || '').split('.').pop();
  const uploadKey = `user/${userId}/${imageUUID}.${imageFileExtension}`;
  const persistedKey = `user/${encodeURIComponent(userId)}/${imageUUID}.${imageFileExtension}`;

  const body = {
    key: uploadKey,
    file_type: imageFile.type
  };

  return new Promise((resolve, reject) => {
    presignedS3({
      body
    })
      .then(async response => {
        const blob = await getFileBlob(imageFile);
        // @ts-ignore
        await axios.put(response?.signedUrl, blob, {
          headers: {
            'Content-Type': imageFile.type
          }
        });
        resolve(persistedKey);
      })
      .catch(error => {
        reject(error);
      });
  });
}

const actions = {
  loadSpeakers({ dispatch, commit }, { eventId, hardRefresh }: { eventId: string; hardRefresh: boolean }) {
    if (hardRefresh) {
      commit('setIsSpeakerListLoading', true);
    }

    return new Promise((resolve, reject) => {
      listEventRoles({
        queryParams: {
          offset: 0,
          role: 4,
          event: eventId
        }
      })
        .then(speakersData => {
          const speakerList = speakersData as Speaker[];
          commit('setSpeakers', transformServerSpeakers(speakerList));
          commit('setTotalSpeakerCount', speakerList.length);

          if (hardRefresh) {
            commit('setIsSpeakerListLoading', false);
          }

          resolve('success');
        })
        .catch(error => {
          dispatch(
            'studioToast/triggerToast',
            { msg: 'Error Loading Speakers, Please Try Again', mode: 'error' },
            { root: true }
          );

          if (hardRefresh) {
            commit('setIsSpeakerListLoading', false);
          }

          reject(error);
        });
    });
  },

  async deleteSpeaker({ commit }, speakerId: string) {
    return new Promise((resolve, reject) => {
      destroyEventRoles({
        // @ts-ignore
        id: speakerId
      })
        .then(() => {
          commit('deleteSpeaker', speakerId);
          resolve(speakerId);
        })
        .catch(error => {
          reject(error);
        });
    });
  },

  async reorderSpeakers({ commit, rootGetters }, speakers: Speaker[]) {
    const payload = {
      updated_ranks: speakers.map((speaker, index) => {
        return {
          event_role: speaker.id,
          rank: index
        };
      })
    };

    const prevSpeakers = rootGetters['studioSpeakers/getSpeakers'];
    commit('reorderSpeakers', speakers);

    return new Promise((resolve, reject) => {
      updateRanksEventRoles({
        // @ts-ignore
        body: payload
      })
        .then(() => {
          resolve('success');
        })
        .catch((error: any) => {
          commit('reorderSpeakers', prevSpeakers);
          reject(error);
        });
    });
  },

  async addSpeaker(
    { rootState, commit },
    {
      user,
      file,
      broadcastIds,
      event_app_role,
      send_calendar_invite,
      send_email_invite
    }: {
      user: User;
      file: File;
      broadcastIds: string[];
      event_app_role?: number;
      send_calendar_invite?: boolean;
      send_email_invite?: boolean;
    }
  ) {
    const eventId: string = rootState.studioEventList.currentEventId;

    return new Promise((resolve, reject) => {
      uploadUserImage({ imageFile: file, userId: user.id })
        .then(imageURL => {
          if (imageURL) {
            // @ts-ignore
            user.profile_picture_url = imageURL;
          }

          return createEventRoles({
            body: {
              event: eventId,
              role: 4,
              broadcasts: broadcastIds,
              user,
              event_app_role,
              send_calendar_invite,
              send_email_invite
            }
          });
        })
        .then((eventRole: EventRole) => {
          commit(
            'studioAgenda/filterSpeakerInBroadcasts',
            {
              broadcastIds: eventRole.broadcasts,
              speakerId: eventRole.user.id
            },
            { root: true }
          );

          resolve(eventRole);
        })
        .catch(reject);
    });
  },

  async updateUser({ dispatch }, { eventId, userId, user }: { eventId: string; userId: string; user: User }) {
    const payload = {
      ...user,
      org_user: {
        event: eventId,
        org_user_data: user
      }
    };
    return new Promise((resolve, reject) => {
      dispatch(
        'makeapicall',
        {
          url: `/core/user/${userId}/`,
          method: 'PATCH',
          body: JSON.stringify(payload)
        },
        { root: true }
      )
        .then(updatedUser => {
          resolve(updatedUser);
        })
        .catch(error => {
          reject(error);
        });
    });
  },

  async updateSpeaker(
    { dispatch, state, commit, rootGetters },
    { speakerId, user, file }: { speakerId: string; user: User; file: File }
  ) {
    const userId = state.speakers[speakerId].user.id;
    const eventId = rootGetters['studioEventList/getCurrentEvent'].id;

    return new Promise((resolve, reject) => {
      if (!file) {
        dispatch('updateUser', { userId, user, eventId })
          .then(updatedUser => {
            commit('updateSpeaker', {
              speakerId,
              speaker: { user: updatedUser }
            });
            resolve(updatedUser);
          })
          .catch(error => {
            reject(error);
          });
      } else {
        uploadUserImage({ imageFile: file, userId })
          .then(async imageURL => {
            const payload = {
              ...user,
              profile_picture_url: imageURL
            };
            const updatedUser = await dispatch('updateUser', {
              eventId,
              userId,
              user: payload
            });
            commit('updateSpeaker', {
              speakerId,
              speaker: { user: updatedUser }
            });
            resolve(updatedUser);
          })
          .catch(error => {
            reject(error);
          });
      }
    });
  },

  async partialUpdateSpeakerEventRole(
    { commit, state },
    { speakerId, isHidden, broadcastIds }: { speakerId: string; isHidden: boolean; broadcastIds: string[] }
  ) {
    return new Promise((resolve, reject) => {
      partialUpdateEventRoles({
        id: parseInt(speakerId, 10),
        body: {
          hidden: isHidden,
          broadcasts: broadcastIds
        }
      })
        .then(updatedSpeaker => {
          commit('updateSpeaker', {
            speakerId,
            speaker: { hidden: updatedSpeaker.hidden }
          });

          commit(
            'studioAgenda/filterSpeakerInBroadcasts',
            {
              broadcastIds: updatedSpeaker.broadcasts,
              speakerId: state.speakers[speakerId].user.id
            },
            { root: true }
          );

          resolve(updatedSpeaker);
        })
        .catch(error => {
          reject(error);
        });
    });
  },

  async partialUpdateEventRoles({ commit, state }, payload: PartialUpdateEventRolesV2Props) {
    return new Promise((resolve, reject) => {
      partialUpdateEventRolesV2(payload)
        .then(updatedSpeaker => {
          commit(
            'studioAgenda/filterSpeakerInBroadcasts',
            {
              broadcastIds: updatedSpeaker.broadcasts,
              speakerId: state.speakers[payload.id].user.id
            },
            { root: true }
          );

          resolve(updatedSpeaker);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  },

  async getMagicLinkUrl({ rootGetters }, email: string) {
    const currentEventId = rootGetters['studioEventList/getCurrentEvent'].id;

    return new Promise((resolve, reject) => {
      magiclinkUrlUser({
        queryParams: {
          email,
          event_id: currentEventId
        }
      })
        .then(response => {
          resolve(response.url);
        })
        .catch((error: any) => {
          let msg = error.message;
          if (error.message === 'User is not registered for the event') {
            msg = "We're generating your magic link! This may take up to 20 minutes";
          }
          reject(new Error(msg));
        });
    });
  },
  async getMagicLinkUrlByUserId({ rootGetters }, userId: string) {
    const currentEventId = rootGetters['studioEventList/getCurrentEvent'].id;

    return new Promise((resolve, reject) => {
      magiclinkUrlUser({
        queryParams: {
          user_id: userId,
          event_id: currentEventId
        }
      })
        .then(response => {
          resolve(response.url);
        })
        .catch((error: any) => {
          let msg = error.message;
          if (error.message === 'User is not registered for the event') {
            msg = "We're generating your magic link! This may take up to 20 minutes";
          }
          reject(new Error(msg));
        });
    });
  },
  async fetchSpeakerEmailConfig({ dispatch, commit, rootGetters }, payload?: { eventId?: string }) {
    const currentEventId = payload?.eventId ?? rootGetters['studioEventList/getCurrentEvent'].id;

    commit('setIsSpeakerEmailConfigLoading', true);
    return new Promise(resolve => {
      listEmail({
        queryParams: {
          event: currentEventId,
          email_type: 'SpkInvite'
        }
      })
        .then(response => {
          commit('setSpeakerInviteEmailConfig', response[0]);
          resolve(response);
        })
        .catch(error => {
          dispatch(
            'studioToast/triggerToast',
            { msg: error.message || 'Error Loading Speakers, Please Try Again', mode: 'error' },
            { root: true }
          );
        })
        .finally(() => {
          commit('setIsSpeakerEmailConfigLoading', false);
        });
    });
  },
  async loadSpeakerRoles({ commit, dispatch }) {
    return new Promise(resolve => {
      listRole({
        queryParams: {
          realm: 'event_app',
          role_group: 'Speaker Roles',
          limit: 10
        }
      })
        .then(roles => {
          const updatedSpeakerRoles =
            (roles.results || []).map(role => {
              return {
                ...role,
                priority: predeterminedSpeakerRoles.indexOf(role.name as SpeakerRoleName)
              };
            }) || [];

          commit('setSpeakerRolesMap', updatedSpeakerRoles);
          resolve(updatedSpeakerRoles);
        })
        .catch(error => {
          dispatch(
            'studioToast/triggerToast',
            { msg: error.message || 'Error Loading Speaker Roles, Please Try Again', mode: 'error' },
            { root: true }
          );
        });
    });
  },
  async loadSystemUserRoles({ commit, dispatch }) {
    return new Promise(resolve => {
      listRole({
        queryParams: {
          realm: 'event_app',
          role_group: 'Speaker Roles',
          limit: 100,
          is_custom: false // no custom, becuase for RS we are not using custom roles
        }
      })
        .then(roles => {
          const updatedSpeakerRoles =
            (roles.results || []).map(role => {
              return {
                ...role,
                priority: predeterminedSpeakerRoles.indexOf(role.name as SpeakerRoleName)
              };
            }) || [];

          commit('setSystemUserRolesMap', updatedSpeakerRoles);
          resolve(updatedSpeakerRoles);
        })
        .catch(error => {
          dispatch(
            'studioToast/triggerToast',
            { msg: error.message || 'Error Loading Speaker Roles, Please Try Again', mode: 'error' },
            { root: true }
          );
        });
    });
  },
  async fetchDefaultSpeakerRole({ commit, dispatch, rootGetters }, payload?: { eventId?: string }) {
    const currentEventId = payload?.eventId ?? rootGetters['studioEventList/getCurrentEvent'].id;

    return new Promise(resolve => {
      listFetchDefaultRoles({
        id: currentEventId,
        queryParams: {}
      })
        .then(defaultRoles => {
          const defaultSpeakerRoleKey = Object.keys(defaultRoles as Record<string, string>).find(
            role => role === 'Speaker Roles'
          );
          const defaultSpeakerRole = defaultSpeakerRoleKey
            ? (defaultRoles as Record<string, string>)[defaultSpeakerRoleKey]
            : null;
          commit('setDefaultSpeakerRole', defaultSpeakerRole);
          resolve(defaultSpeakerRole);
        })
        .catch(error => {
          dispatch(
            'studioToast/triggerToast',
            { msg: error.message || 'Error Loading Speaker Roles, Please Try Again', mode: 'error' },
            { root: true }
          );
        });
    });
  },
  async updateDefaultSpeakerRole(
    { commit, dispatch },
    { role, payload }: { role: ExtendedRole; payload: PartialUpdateEventDefaultRolesProps }
  ) {
    return new Promise(resolve => {
      partialUpdateEventDefaultRoles(payload)
        .then(defaultSpeakerRole => {
          commit('partialUpdateDefaultSpeakerRole', {
            id: defaultSpeakerRole.id,
            payload: {
              id: defaultSpeakerRole.role,
              name: role.name
            }
          });
          resolve(defaultSpeakerRole);
        })
        .catch(error => {
          dispatch(
            'studioToast/triggerToast',
            { msg: error.message || 'Error Loading Speaker Roles, Please Try Again', mode: 'error' },
            { root: true }
          );
        });
    });
  }
};

export default actions;
